import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Unstable_Grid2'
import { Buildings as BuildingsIcon } from '@phosphor-icons/react/dist/ssr/Buildings'

import { useWorkspaces } from '@/hooks/use-workspaces'

export interface OperatorRequiredProps {
	children: ReactNode
	pageName?: string
}

/**
 * For users who can switch operators, renders a skeleton placeholder instead of `children` while the current
 * operator is loading or when the "All" operator is selected, so operator-wide metric queries are never issued.
 * Users without operator switching always get `children` unchanged.
 */
export function OperatorRequired({ children, pageName = 'this page' }: OperatorRequiredProps) {
	const { canSwitchOperator, isAllOperator, isLoading } = useWorkspaces()

	if (!canSwitchOperator) {
		return <>{children}</>
	}

	if (isAllOperator) {
		return <OperatorRequiredPlaceholder pageName={pageName} />
	}

	if (isLoading) {
		return <OperatorRequiredPlaceholder />
	}

	return <>{children}</>
}

function OperatorRequiredPlaceholder({ pageName }: { pageName?: string }) {
	return (
		<Box
			data-test="operator-required-placeholder"
			sx={{
				maxWidth: 'var(--Content-maxWidth)',
				m: 'var(--Content-margin)',
				p: 'var(--Content-padding)',
				width: 'var(--Content-width)',
				position: 'relative',
				overflow: 'hidden',
			}}
		>
			{pageName ? (
				<Box
					sx={{
						position: { xs: 'static', md: 'absolute' },
						inset: 0,
						zIndex: 1,
						display: 'flex',
						alignItems: 'flex-start',
						justifyContent: 'center',
						pt: { xs: 1, md: 20 },
						pb: { xs: 4, md: 0 },
						px: { xs: 0, md: 3 },
						pointerEvents: 'none',
					}}
				>
					<Card elevation={8} sx={{ maxWidth: 480, width: '100%', pointerEvents: 'auto' }}>
						<CardContent>
							<Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center', py: 2 }}>
								<Box
									sx={{
										alignItems: 'center',
										bgcolor: 'var(--mui-palette-primary-main)',
										borderRadius: '50%',
										color: 'var(--mui-palette-primary-contrastText)',
										display: 'flex',
										height: 56,
										justifyContent: 'center',
										width: 56,
									}}
								>
									<BuildingsIcon fontSize="var(--icon-fontSize-lg)" />
								</Box>
								<Typography variant="h6" data-test="page-heading">
									Please select an operator to view {pageName}
								</Typography>
								<Typography color="text.secondary" variant="body2">
									Metrics are not available across all operators. Use the operator switcher in the sidebar to choose
									one.
								</Typography>
							</Stack>
						</CardContent>
					</Card>
				</Box>
			) : null}
			<Stack spacing={4} pt={{ xs: 1, sm: 0 }} sx={{ opacity: pageName ? 0.5 : 1 }} aria-hidden>
				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'center' }}>
					<Box sx={{ flex: '1 1 auto' }}>
						<Skeleton variant="text" width={240} height={40} />
					</Box>
					<Skeleton variant="rounded" width={220} height={40} />
				</Stack>
				<Grid container spacing={4}>
					{[0, 1, 2].map((index) => (
						<Grid md={4} xs={12} key={index}>
							<Card>
								<CardContent>
									<Stack direction="row" spacing={3} sx={{ alignItems: 'center' }}>
										<Skeleton variant="circular" width={48} height={48} />
										<Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
											<Skeleton variant="text" width="40%" />
											<Skeleton variant="text" width="60%" height={32} />
										</Stack>
									</Stack>
								</CardContent>
							</Card>
						</Grid>
					))}
					<Grid xs={12}>
						<Card>
							<CardContent>
								<Skeleton variant="text" width={160} height={28} />
								<Skeleton variant="rounded" height={280} sx={{ mt: 2 }} />
							</CardContent>
						</Card>
					</Grid>
					<Grid lg={7} xs={12}>
						<Card>
							<CardContent>
								<Skeleton variant="text" width={160} height={28} />
								<Stack spacing={2} sx={{ mt: 2 }}>
									{[0, 1, 2, 3, 4].map((index) => (
										<Skeleton variant="text" key={index} />
									))}
								</Stack>
							</CardContent>
						</Card>
					</Grid>
					<Grid lg={5} xs={12}>
						<Card>
							<CardContent>
								<Skeleton variant="text" width={160} height={28} />
								<Stack spacing={2} sx={{ mt: 2 }}>
									{[0, 1, 2, 3, 4].map((index) => (
										<Skeleton variant="text" key={index} />
									))}
								</Stack>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Stack>
		</Box>
	)
}
